// Auto-generated content for the Policies & Disclosures page.
// Kept as a plain JS module so both React (web) can import it directly.
// Mirror lives at /app/mobile/src/content/policies.ts — keep in sync manually.

const CONTACT_BLOCK = `MyTravelBio LLC (d/b/a TravelBio)
Website: https://mytravelbio.com
Email: info@mytravelbio.com
Business Location: Vermont, United States`;

export const PRIVACY_POLICY = `TravelBio Privacy Policy
Effective Date: July 4, 2026

1. Introduction
Welcome to TravelBio. TravelBio is a product owned and operated by MyTravelBio LLC ("MyTravelBio LLC," "we," "our," or "us").
TravelBio is designed to help users discover destinations, plan vacations, organize travel ideas, preserve travel memories, and access third-party travel booking opportunities.
Your privacy is important to us. This Privacy Policy explains what information we collect, how we use it, when we share it, and the choices you have regarding your information.
By using the TravelBio website or iOS application, you agree to the practices described in this Privacy Policy.

2. Information We Collect
Depending on how you use TravelBio, we may collect the following information.

Account Information
When you create an account, we may collect:
- Name
- Email address
- Username
- Password (stored securely through our authentication provider)
- Profile photo (optional)

Travel Content
You may choose to create or upload:
- Travel journals
- Destination wish lists
- Saved trips
- Trip itineraries
- Photos
- Notes
- Ratings
- Other travel-related content you voluntarily provide

Device Information
When you use TravelBio, we may automatically collect:
- Device type
- Operating system version
- App version
- Browser type (website only)
- IP address
- General location derived from your IP address
- Crash reports
- Diagnostic information

Usage Information
We may collect information about how you interact with TravelBio, including:
- Features used
- Destinations viewed
- Searches performed
- Saved destinations
- Time spent within the app
- Pages visited

Cookies and Similar Technologies
Our website may use cookies and similar technologies to improve functionality, remember preferences, analyze traffic, and enhance your experience. Please see our Cookie Policy for additional information.

3. How We Use Your Information
We use your information to:
- Create and manage your account
- Provide TravelBio services
- Save your travel journals and memories
- Display your saved destinations and trips
- Improve our products and features
- Respond to customer support requests
- Monitor app performance and security
- Detect fraud or misuse
- Comply with legal obligations
We may also use aggregated or anonymized data to improve TravelBio. This information does not identify individual users.

4. Third-Party Services
TravelBio works with trusted third-party providers that help us operate the platform.
Depending on the services available at the time you use TravelBio, these providers may include:
- Authentication providers
- Cloud hosting providers
- Analytics providers
- Crash reporting services
- Affiliate travel partners
- Mapping services
- Image storage providers
When you choose to book travel through TravelBio, you may be directed to a third-party booking provider. Your booking is completed directly with that provider, and their privacy policy and terms will govern your interaction with them.
TravelBio does not control the privacy practices of third-party websites or booking providers.

5. Affiliate Links
Some travel recommendations within TravelBio contain affiliate links.
If you click an affiliate link and complete a qualifying purchase, TravelBio may receive a commission at no additional cost to you.
Your decision to use these links is entirely voluntary.

6. Sharing Information
We do not sell your personal information.
We may share information only when necessary:
- With service providers who help operate TravelBio
- With affiliate partners when you voluntarily access booking services
- To comply with legal obligations
- To protect the rights, safety, or security of TravelBio or others
- As part of a merger, acquisition, or sale of assets

7. User Content
You retain ownership of the travel journals, photographs, notes, and other content you upload.
By uploading content to TravelBio, you grant us a limited, non-exclusive, worldwide license to store, display, process, and transmit that content solely for the purpose of providing and improving the TravelBio service.
You remain responsible for ensuring you have the rights to upload any content you submit.

8. Data Security
We use commercially reasonable administrative, technical, and physical safeguards to protect your information.
However, no internet transmission or electronic storage method is completely secure. While we strive to protect your information, we cannot guarantee absolute security.

9. Data Retention
We retain your information for as long as your account remains active or as needed to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements.
If you delete your account, we will make reasonable efforts to remove or anonymize your personal information within a reasonable period, unless we are legally required to retain certain information.

10. Your Choices
Depending on your location, you may have the right to:
- Access your personal information
- Correct inaccurate information
- Delete your account
- Request a copy of your information where applicable
- Manage email communications
- Control certain device permissions
Requests may be submitted through our support contact listed below.

11. Children's Privacy
TravelBio is not intended for children under the age of 13.
We do not knowingly collect personal information from children under 13. If we become aware that such information has been collected, we will take reasonable steps to delete it.

12. International Users
TravelBio is operated from the United States.
If you access TravelBio from outside the United States, you understand that your information may be transferred to and processed within the United States.

13. Changes to This Privacy Policy
We may update this Privacy Policy from time to time.
When material changes are made, we will update the Effective Date at the top of this policy. Continued use of TravelBio after changes become effective constitutes acceptance of the updated Privacy Policy.

14. Contact Us
If you have questions about this Privacy Policy or our privacy practices, please contact us at:
${CONTACT_BLOCK}`;

export const TERMS_OF_SERVICE = `TravelBio Terms of Service
Effective Date: July 4, 2026

1. Acceptance of These Terms
Welcome to TravelBio. TravelBio is a product owned and operated by MyTravelBio LLC ("MyTravelBio LLC," "we," "our," or "us").
These Terms of Service ("Terms") govern your access to and use of the TravelBio website and iOS application, including any related services, features, content, and functionality we provide.
By creating an account, accessing, or using TravelBio, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use TravelBio.

2. Eligibility
You must be at least 13 years old to use TravelBio.
If you are under the age of 18, you may only use TravelBio with the involvement and permission of a parent or legal guardian.
By using TravelBio, you represent that you meet these eligibility requirements.

3. Your Account
You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.
You agree to:
- Provide accurate and current information.
- Keep your login credentials secure.
- Notify us promptly if you believe your account has been accessed without authorization.
- Not create accounts using false identities or impersonate another person.
We reserve the right to suspend or terminate accounts that violate these Terms.

4. TravelBio Services
TravelBio provides tools to help users:
- Discover travel destinations
- Save travel ideas
- Build bucket lists
- Create travel journals
- Organize trips
- Preserve travel memories
- Access third-party travel booking opportunities
TravelBio does not own, operate, or control the hotels, airlines, vacation rentals, cruise lines, attractions, or other travel providers displayed through our platform unless explicitly stated otherwise.

5. Third-Party Bookings
TravelBio may provide links to third-party booking providers.
When you choose to book travel, your reservation is completed directly with the applicable provider.
TravelBio is not responsible for:
- Pricing
- Availability
- Booking confirmations
- Reservation changes
- Cancellations
- Refunds
- Supplier policies
- Travel disruptions
Any questions regarding a reservation should be directed to the booking provider.

6. Affiliate Relationships
Some booking links and recommendations may be affiliate links.
If you complete a qualifying purchase through one of these links, TravelBio may receive a commission at no additional cost to you.
Our recommendations are intended to help users discover travel opportunities and are not influenced solely by affiliate compensation.

7. User Content
You retain ownership of the content you create and upload, including:
- Photos
- Travel journals
- Notes
- Reviews
- Itineraries
- Lists
- Other travel-related content
By submitting content, you grant TravelBio a non-exclusive, worldwide, royalty-free license to host, store, reproduce, display, and transmit that content solely as necessary to operate, improve, and provide the TravelBio service.
You represent that you have the legal right to upload any content you submit.

8. Prohibited Conduct
You agree not to:
- Violate any applicable laws.
- Upload unlawful, fraudulent, or misleading content.
- Upload content that infringes another person's intellectual property rights.
- Attempt to gain unauthorized access to TravelBio systems.
- Interfere with the operation or security of TravelBio.
- Use automated systems to scrape or collect information without authorization.
- Introduce viruses, malware, or harmful code.
- Use TravelBio in a way that harms other users.
We reserve the right to remove content or suspend accounts that violate these Terms.

9. Intellectual Property
TravelBio, including its name, logo, branding, software, design, graphics, text, and original content, is owned by TravelBio or its licensors and is protected by applicable intellectual property laws.
Except as expressly permitted, you may not copy, reproduce, modify, distribute, or create derivative works from TravelBio content without prior written permission.

10. Availability of Service
We strive to keep TravelBio available and functioning properly.
However, we do not guarantee uninterrupted, error-free, or continuous availability.
We may modify, suspend, or discontinue features at any time without prior notice.

11. Disclaimer of Warranties
TravelBio is provided on an "as is" and "as available" basis.
To the fullest extent permitted by law, we disclaim all warranties, whether express or implied, including warranties of merchantability, fitness for a particular purpose, non-infringement, and uninterrupted availability.
We do not guarantee the accuracy, completeness, or availability of travel information, pricing, destination details, or booking opportunities displayed within TravelBio.

12. Limitation of Liability
To the fullest extent permitted by law, TravelBio and its owners, officers, employees, contractors, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or relating to:
- Your use of TravelBio.
- Third-party booking providers.
- Travel cancellations or delays.
- Supplier actions or omissions.
- Lost profits.
- Lost data.
- Personal injury.
- Property damage.
- Travel disruptions.
Where liability cannot be excluded by law, our total liability shall not exceed the amount you paid directly to TravelBio for the applicable service during the twelve months preceding the event giving rise to the claim.

13. Indemnification
You agree to defend, indemnify, and hold harmless TravelBio and its affiliates from any claims, damages, losses, liabilities, costs, and expenses arising out of:
- Your use of TravelBio.
- Your violation of these Terms.
- Your violation of another person's rights.
- Content you upload or submit.

14. Account Suspension or Termination
We may suspend or terminate your account if:
- You violate these Terms.
- You engage in fraudulent or abusive conduct.
- Your use creates security risks.
- We are required to do so by law.
You may stop using TravelBio or delete your account at any time.

15. Changes to These Terms
We may update these Terms periodically.
When material changes are made, we will revise the Effective Date at the top of this document.
Your continued use of TravelBio after changes become effective constitutes acceptance of the updated Terms.

16. Governing Law
These Terms shall be governed by and interpreted in accordance with the laws of the State of Vermont, without regard to its conflict of law principles.
Any disputes arising under these Terms shall be resolved in the appropriate state or federal courts located in Vermont, unless applicable law provides otherwise.

17. Contact Us
If you have questions regarding these Terms, please contact:
${CONTACT_BLOCK}`;

export const AFFILIATE_DISCLOSURE = `TravelBio Affiliate Disclosure
Effective Date: July 4, 2026

1. Purpose
TravelBio believes in transparency with our users. This Affiliate Disclosure explains how we may earn compensation through certain links and travel recommendations available on our platform.
By using TravelBio, you acknowledge and understand the information provided in this disclosure.

2. Affiliate Relationships
TravelBio participates in affiliate marketing programs with select travel providers and partners.
When you click on certain links within the TravelBio website or iOS application and complete a qualifying purchase or reservation, we may receive a commission or other form of compensation from the travel provider.
This compensation comes from the affiliate partner and does not increase the price you pay.

3. How Affiliate Links Work
Some hotels, vacation rentals, flights, cruises, activities, rental cars, vacation packages, or other travel services displayed within TravelBio may contain affiliate links.
When you select one of these links, you may be redirected to a third-party website or booking platform to complete your reservation.
TravelBio does not process bookings directly at this time.

4. Our Commitment to You
Our goal is to help travelers discover destinations, organize trips, preserve memories, and connect with reputable travel providers.
Affiliate relationships do not determine every recommendation presented within TravelBio. We strive to present useful travel options based on available information and user preferences whenever applicable.

5. Third-Party Providers
Reservations completed through affiliate partners are subject to the terms, conditions, privacy policies, pricing, cancellation policies, and customer support procedures of the applicable travel provider.
TravelBio is not responsible for:
- Reservation confirmations
- Supplier availability
- Pricing changes
- Booking modifications
- Cancellations
- Refunds
- Customer service provided by third-party partners
Questions regarding a reservation should be directed to the provider with whom you completed your booking.

6. Amazon Associates Program
TravelBio is a participant in the Amazon Services LLC Associates Program, an affiliate advertising program designed to provide a means for sites to earn advertising fees by advertising and linking to Amazon.com.
As required by the Amazon Operating Agreement, links to Amazon.com or its affiliated products may earn TravelBio a commission at no additional cost to you.
Product recommendations surfaced within the TravelBio Backpack feature (and any other Amazon-linked surface in the app) may be affiliate links and are identified with a small disclosure adjacent to the link.
Amazon and the Amazon logo are trademarks of Amazon.com, Inc. or its affiliates.

7. Future Affiliate Partners
TravelBio may add, remove, or change affiliate partners over time without updating this disclosure each time, provided this Affiliate Disclosure remains accurate regarding how affiliate relationships function.

8. Questions
If you have questions regarding this Affiliate Disclosure, please contact us:
${CONTACT_BLOCK}`;

export const BOOKING_PRICING_DISCLAIMER = `TravelBio Booking & Pricing Disclaimer
Effective Date: July 4, 2026

1. Purpose
TravelBio is designed to help users discover destinations, organize travel plans, preserve travel memories, and connect with third-party travel providers.
This Booking & Pricing Disclaimer explains the role TravelBio plays in the booking process and outlines important information regarding travel reservations, pricing, and availability.

2. TravelBio Is Not a Travel Supplier
Unless expressly stated otherwise, TravelBio is not a travel agency, hotel, airline, cruise line, vacation rental company, tour operator, transportation provider, or other travel supplier.
TravelBio does not own, operate, or control the accommodations, transportation, attractions, activities, or services offered by third-party providers displayed within our platform.

3. Third-Party Reservations
At this time, reservations made through links available on TravelBio are completed directly with independent third-party travel providers.
When you choose to make a reservation, you will be redirected to the provider's website or booking platform to complete your purchase.
Your reservation is governed by that provider's:
Terms and Conditions
Privacy Policy
Cancellation Policy
Refund Policy
Customer Service Procedures
Pricing and Availability
TravelBio is not a party to the booking agreement between you and the travel provider.

4. Pricing and Availability
Travel pricing changes frequently.
Prices, discounts, taxes, fees, availability, promotions, and inventory may change at any time without notice.
TravelBio cannot guarantee that prices displayed within the app or on our website will remain available until your reservation is completed.
The final price is determined solely by the booking provider at the time of purchase.

5. Accuracy of Information
We make reasonable efforts to provide accurate destination information, descriptions, images, amenities, and travel recommendations.
However, TravelBio does not guarantee that all information is complete, current, or error-free.
Information provided by third-party suppliers may change without notice.
Users should verify important details directly with the travel provider before making travel decisions.

6. Changes, Cancellations, and Refunds
TravelBio does not control or process:
Reservation changes
Cancellations
Refund requests
Credits
Travel vouchers
Supplier policies
If you need to modify or cancel a reservation, you should contact the travel provider through whom your reservation was made.

7. Travel Disruptions
TravelBio is not responsible for losses or inconveniences resulting from events beyond our control, including but not limited to:
Weather events
Natural disasters
Flight delays or cancellations
Hotel overbookings
Transportation interruptions
Government travel restrictions
Public health emergencies
Labor strikes
Supplier insolvency
Technical outages affecting third-party providers
Travelers are encouraged to review the policies of their selected provider and consider purchasing travel insurance when appropriate.

8. No Guarantee of Availability
Displaying a destination, hotel, vacation rental, cruise, flight, attraction, or activity within TravelBio does not guarantee that it will be available for booking.
Availability is determined solely by the applicable travel provider.

9. Future Services
TravelBio may expand its services in the future to offer additional travel planning or booking capabilities.
If our role in the booking process changes, this disclaimer will be updated to reflect those changes.

10. Questions
If you have questions regarding this Booking & Pricing Disclaimer, please contact us:
${CONTACT_BLOCK}`;

export const COOKIE_POLICY = `TravelBio Cookie Policy
Effective Date: July 4, 2026

1. Purpose
This Cookie Policy explains how MyTravelBio LLC ("MyTravelBio LLC," "we," "our," or "us"), the operator of TravelBio, uses cookies and similar technologies on our website.
This policy should be read together with our Privacy Policy.

2. What Are Cookies?
Cookies are small text files stored on your device when you visit a website. They help websites recognize your browser, remember preferences, improve functionality, and better understand how visitors use the site.
Our iOS application does not use browser cookies in the same manner as our website. However, the app may use similar technologies provided by Apple or trusted third-party service providers to improve functionality, security, and performance.

3. Types of Cookies We May Use
Essential Cookies
These cookies are necessary for our website to function properly. They may enable features such as:
Secure logins
Account authentication
Session management
Security protections
Because these cookies are necessary for the operation of the website, they generally cannot be disabled through our site.
Functional Cookies
These cookies help remember your preferences, including:
Language preferences
Saved settings
User experience improvements
These cookies help provide a more personalized experience.
Analytics Cookies
We may use analytics technologies to better understand how visitors use our website.
These tools may collect information such as:
Pages visited
Time spent on pages
Navigation patterns
Device type
Browser type
General geographic region
Referral sources
This information helps us improve TravelBio and understand which features are most useful to our users.
Performance Cookies
Performance cookies help us identify technical issues, monitor website performance, and improve reliability.
These cookies may collect anonymous technical information regarding page loading times, errors, and system performance.

4. Third-Party Technologies
TravelBio may work with trusted third-party service providers that use cookies or similar technologies to support services such as:
Website hosting
Authentication
Analytics
Security
Performance monitoring
These providers maintain their own privacy practices and policies.

5. Managing Cookies
Most web browsers allow you to:
View stored cookies
Delete existing cookies
Block future cookies
Receive notifications before cookies are stored
Please note that disabling certain cookies may affect the functionality of portions of the TravelBio website.

6. Do Not Track
Some browsers offer a "Do Not Track" setting.
Because there is currently no universally accepted standard for responding to Do Not Track signals, TravelBio does not currently respond differently based on those browser settings.

7. Updates to This Policy
We may update this Cookie Policy from time to time.
When material changes are made, we will revise the Effective Date shown at the top of this document.
Continued use of our website after updates become effective constitutes acceptance of the revised Cookie Policy.

8. Contact Us
If you have questions regarding this Cookie Policy, please contact us:
${CONTACT_BLOCK}`;

export const USER_CONTENT_COPYRIGHT_POLICY = `TravelBio User Content & Copyright Policy
Effective Date: July 4, 2026

1. Purpose
TravelBio is built to help travelers document experiences, preserve memories, organize trips, and share their adventures. This User Content & Copyright Policy explains your rights and responsibilities regarding content you create, upload, or share through TravelBio.
By using TravelBio, you agree to comply with this policy.

2. Your Ownership
You retain ownership of the content you create and upload to TravelBio.
This includes, but is not limited to:
Travel journals
Photographs
Videos
Notes
Reviews
Itineraries
Bucket lists
Captions
Profile information
Other original content you submit
Nothing in this policy transfers ownership of your content to TravelBio.

3. License Granted to TravelBio
To operate the TravelBio platform, you grant TravelBio a limited, non-exclusive, worldwide, royalty-free, revocable license to:
Store your content
Display your content within your account
Process your content
Back up your content
Transmit your content as necessary to provide the TravelBio service
Make technical modifications required to display your content across supported devices and platforms
This license exists only for the purpose of operating, maintaining, improving, and securing TravelBio.
You may terminate this license by deleting your content or closing your account, subject to reasonable backup retention periods and any legal obligations requiring us to retain certain information.

4. Your Responsibilities
You are solely responsible for the content you upload.
By submitting content, you represent that:
You own the content or have permission to use it.
Your content does not infringe another person's copyright, trademark, privacy, or other legal rights.
Your content complies with applicable laws.
Your content does not contain malicious software or harmful code.

5. Prohibited Content
You may not upload, post, or share content that:
Violates any law.
Infringes another person's intellectual property rights.
Contains defamatory, fraudulent, or misleading information.
Promotes illegal activity.
Contains malware, viruses, or harmful code.
Invades another person's privacy.
Is intended to harass, threaten, or intimidate others.
Contains obscene, sexually explicit, or otherwise unlawful material.
Impersonates another individual or organization.
TravelBio reserves the right to remove content that violates this policy.

6. Copyright Complaints
TravelBio respects the intellectual property rights of others.
If you believe content available through TravelBio infringes your copyright, please contact us with:
Your name and contact information.
A description of the copyrighted work.
The location of the allegedly infringing material.
A statement that you have a good-faith belief the use is unauthorized.
A statement, made under penalty of perjury, that the information provided is accurate and that you are the copyright owner or authorized to act on the owner's behalf.
Your physical or electronic signature.
Upon receiving a valid notice, TravelBio will investigate and take appropriate action, which may include removing the material or restricting access to it.

7. Repeat Infringers
TravelBio reserves the right to suspend or terminate the accounts of users who repeatedly violate intellectual property rights or repeatedly submit infringing material.

8. Content Removal
TravelBio may remove or restrict access to content that:
Violates this policy.
Violates our Terms of Service.
Creates legal or security risks.
Is subject to a valid legal request.
Is otherwise determined, in our reasonable judgment, to be harmful to the platform or its users.
Whenever practical, we may notify the affected user before or after removing content.

9. Backup of Content
While we strive to maintain reliable backups, users are responsible for maintaining copies of their own travel journals, photographs, and other important content.
TravelBio does not guarantee that uploaded content will never be lost due to technical failures or circumstances beyond our control.

10. Feedback
If you voluntarily submit ideas, suggestions, feature requests, or other feedback regarding TravelBio, you agree that we may use that feedback without obligation to compensate you or provide attribution.

11. Questions
If you have questions regarding this User Content & Copyright Policy, please contact us:
${CONTACT_BLOCK}`;

export const TRAVEL_INFORMATION_DISCLAIMER = `TravelBio Travel Information Disclaimer
Effective Date: July 4, 2026

1. Purpose
TravelBio is designed to inspire travel, help users discover destinations, organize trips, preserve travel memories, and connect with third-party travel providers.
This Travel Information Disclaimer explains the limitations of the travel information provided through our website and iOS application.

2. Informational Purposes Only
The information available through TravelBio is provided for general informational and planning purposes only.
While we strive to provide useful and accurate information, TravelBio does not guarantee that destination details, travel recommendations, maps, weather information, attraction descriptions, pricing, availability, or other travel-related content will always be accurate, complete, or current.
Travel information may change without notice.

3. Verify Important Information
Before making travel decisions, users should independently verify important information with the appropriate sources, including but not limited to:
Hotels and accommodations
Airlines
Cruise lines
Tour operators
Government agencies
Passport authorities
Visa authorities
Local tourism offices
Official transportation providers
TravelBio is not responsible for decisions made based on information displayed within the platform.

4. International Travel
International travelers are responsible for understanding and complying with all applicable requirements, including:
Passport validity
Visa requirements
Customs regulations
Entry requirements
Health requirements
Vaccination requirements
Currency regulations
Local laws
Travel advisories
Requirements may change without notice and vary by destination.

5. Weather and Natural Events
Weather conditions and natural events can significantly impact travel.
TravelBio does not guarantee weather forecasts or destination conditions and is not responsible for travel interruptions resulting from:
Severe weather
Natural disasters
Wildfires
Flooding
Hurricanes
Earthquakes
Volcanic activity
Other environmental events

6. Health and Safety Information
TravelBio does not provide medical, health, safety, legal, immigration, tax, or financial advice.
Users should consult qualified professionals or the appropriate governmental authorities regarding matters related to:
Health precautions
Vaccinations
Travel insurance
Medical conditions
Legal requirements
Immigration rules
Financial matters

7. Third-Party Information
Some information presented within TravelBio may originate from third-party providers.
Although we make reasonable efforts to present reliable information, we do not control or independently verify all third-party content and cannot guarantee its accuracy or completeness.

8. No Guarantees
TravelBio makes no guarantees regarding:
Destination suitability
Travel experiences
Hotel quality
Airline performance
Attraction availability
Prices
Travel outcomes
User satisfaction
Individual travel experiences may vary.

9. Limitation of Responsibility
To the fullest extent permitted by law, TravelBio shall not be responsible for any loss, expense, inconvenience, injury, delay, cancellation, or damages resulting from reliance on travel information made available through our platform.
Users remain responsible for their own travel planning and decisions.

10. Changes to This Disclaimer
We may update this Travel Information Disclaimer from time to time.
Material changes will be reflected by updating the Effective Date at the top of this document.
Continued use of TravelBio after changes become effective constitutes acceptance of the revised disclaimer.

11. Contact Us
If you have questions regarding this Travel Information Disclaimer, please contact us:
${CONTACT_BLOCK}`;

export const COMMUNITY_GUIDELINES = `TravelBio Community Guidelines
Effective Date: July 4, 2026

1. Our Community
TravelBio exists to inspire exploration, celebrate travel experiences, and help people preserve their memories for years to come.
We want TravelBio to be a welcoming, respectful, and enjoyable place for travelers of all backgrounds. These Community Guidelines explain the standards we expect from everyone who uses our platform.
By using TravelBio, you agree to follow these guidelines.

2. Be Respectful
Treat others with kindness and respect.
We encourage users to share experiences, recommendations, and memories in a positive and constructive manner.
Do not:
Harass or bully others.
Threaten or intimidate anyone.
Use hateful or discriminatory language.
Encourage violence or illegal activity.
Deliberately provoke or target other users.

3. Share Authentic Content
TravelBio is intended for genuine travel experiences and planning.
Please share content that is:
Accurate to the best of your knowledge.
Relevant to travel.
Respectful of local cultures and communities.
Created by you or shared with proper permission.
Avoid posting misleading or intentionally false information.

4. Respect Intellectual Property
Only upload content that you own or have permission to use.
Do not upload copyrighted photographs, videos, music, logos, or other materials without authorization.
Respect the intellectual property rights of creators, businesses, and fellow travelers.

5. Respect Privacy
Help protect everyone's privacy.
Do not share:
Another person's private information without permission.
Personal identification documents.
Financial information.
Medical information.
Private contact information.
Always obtain permission before sharing photos or videos in which another individual is the primary subject.

6. Prohibited Content
The following content is prohibited on TravelBio:
Illegal content.
Fraudulent or deceptive content.
Spam or repetitive postings.
Malware or malicious software.
Sexually explicit material.
Graphic violence.
Content promoting terrorism or criminal activity.
Content that infringes intellectual property rights.
Content intended to scam or mislead users.
Impersonation of another individual or organization.
TravelBio reserves the right to remove prohibited content without notice.

7. Commercial Activity
Unless expressly authorized by TravelBio, users may not:
Advertise products or services.
Solicit business.
Promote affiliate links.
Conduct unauthorized marketing campaigns.
Send spam messages.
Use TravelBio primarily for commercial promotion.

8. Artificial Activity
To maintain a fair experience, users may not:
Create fake accounts.
Manipulate engagement metrics.
Use bots or automated systems without authorization.
Attempt to interfere with the operation of TravelBio.

9. Reporting Content
If you encounter content that violates these Community Guidelines, you may report it through available reporting tools or by contacting us directly.
We review reports in good faith and may take appropriate action based on the information available.

10. Enforcement
Depending on the nature of a violation, TravelBio may:
Remove content.
Restrict visibility of content.
Issue warnings.
Suspend accounts.
Permanently terminate accounts.
Report unlawful activity to appropriate authorities when required or permitted by law.
Our moderation decisions are made at our reasonable discretion and with the goal of maintaining a safe and respectful community.

11. Updates
We may revise these Community Guidelines from time to time to reflect changes to our platform or community standards.
Material updates will be reflected by updating the Effective Date at the top of this document.

12. Contact Us
If you have questions regarding these Community Guidelines, please contact us:
${CONTACT_BLOCK}`;

export const ACCEPTABLE_USE_POLICY = `TravelBio Acceptable Use Policy
Effective Date: July 4, 2026

1. Purpose
This Acceptable Use Policy ("Policy") explains the standards for using the TravelBio website, iOS application, and any related services (collectively, the "Services").
By using TravelBio, you agree to comply with this Policy in addition to our Terms of Service and other applicable policies.

2. Lawful Use
You may use TravelBio only for lawful purposes and in accordance with all applicable laws and regulations.
You agree not to use TravelBio in any manner that could damage, disable, overburden, interfere with, or impair our Services or the experience of other users.

3. Unauthorized Access
You may not:
Attempt to gain unauthorized access to any TravelBio account, server, database, or network.
Circumvent or bypass security measures.
Test or scan our systems for vulnerabilities without our prior written authorization.
Access data that does not belong to you.

4. Automated Activity
Unless expressly authorized by TravelBio, you may not:
Use bots or automated scripts to access the Services.
Scrape or harvest content or user information.
Collect destination data in bulk.
Perform excessive automated searches.
Create accounts through automated means.
Interfere with the normal operation of the platform through automation.
Reasonable indexing by recognized search engines is permitted where applicable.

5. Artificial Intelligence and Data Use
You may not use TravelBio or its content to:
Train artificial intelligence or machine learning models without our written permission.
Copy or extract substantial portions of our destination content, travel recommendations, or proprietary materials for commercial purposes.
Build competing databases or services using TravelBio content.
Reverse engineer or reproduce proprietary features of the platform except where permitted by applicable law.

6. User Content
You may not upload or share content that:
Violates applicable law.
Infringes another person's intellectual property rights.
Contains malware, viruses, or harmful code.
Is fraudulent or deceptive.
Harasses, threatens, or intimidates others.
Promotes illegal activities.
Invades another person's privacy.
Additional standards are described in our Community Guidelines and User Content & Copyright Policy.

7. Commercial Misuse
Without our prior written permission, you may not:
Resell access to TravelBio.
Use TravelBio primarily for unsolicited advertising or marketing.
Promote unauthorized affiliate links.
Misrepresent your relationship with TravelBio.
Use our trademarks, branding, or logos in a manner that suggests sponsorship or endorsement.

8. Reverse Engineering
Except where prohibited by applicable law, you may not:
Reverse engineer, decompile, or disassemble any portion of TravelBio.
Attempt to discover source code.
Copy or imitate proprietary software functionality.
Remove copyright, trademark, or proprietary notices.

9. Security
Users must not knowingly introduce:
Malware
Viruses
Trojan horses
Worms
Ransomware
Harmful scripts
Any technology intended to disrupt or compromise the Services

10. Enforcement
TravelBio may investigate suspected violations of this Policy.
If we determine that this Policy has been violated, we may take appropriate action, including:
Removing content.
Restricting access to certain features.
Suspending or terminating accounts.
Blocking devices or IP addresses where appropriate.
Reporting unlawful activity to law enforcement or other authorities when required or permitted by law.

11. Changes to This Policy
We may update this Acceptable Use Policy from time to time.
Material changes will be reflected by updating the Effective Date at the top of this document.
Continued use of TravelBio after changes become effective constitutes acceptance of the revised Policy.

12. Contact Us
If you have questions regarding this Acceptable Use Policy, please contact us:
${CONTACT_BLOCK}`;

export const ACCESSIBILITY_STATEMENT = `TravelBio Accessibility Statement
Effective Date: July 4, 2026

1. Our Commitment
TravelBio is committed to making our website and iOS application accessible and usable for as many people as possible, including individuals with disabilities.
We believe that everyone should have the opportunity to discover destinations, plan meaningful trips, preserve travel memories, and enjoy the TravelBio experience.
Accessibility is an ongoing effort, and we are continually working to improve the usability of our platform.

2. Accessibility Efforts
As TravelBio evolves, we strive to incorporate accessibility best practices into the design and development of our services.
Our ongoing efforts may include:
Clear and consistent navigation
Readable typography and color contrast
Support for screen readers where practical
Keyboard-accessible website functionality where applicable
Meaningful labels for interactive elements
Responsive layouts across supported devices
Continuous review and improvement of accessibility features
Because TravelBio is continually evolving, accessibility improvements will be made over time as new features are developed.

3. Third-Party Services
Some features available through TravelBio may redirect users to third-party websites or booking providers.
We do not control the accessibility of third-party websites or services and cannot guarantee their accessibility standards.
Questions regarding the accessibility of third-party services should be directed to the applicable provider.

4. Compatibility
TravelBio is designed to support current versions of major web browsers and our supported iOS application.
As additional platforms become available in the future, we will continue working to improve accessibility across supported devices.

5. Feedback
If you experience difficulty accessing any part of TravelBio or have suggestions that could improve accessibility, we encourage you to contact us.
Your feedback helps us identify areas where we can continue improving the experience for all users.

6. Contact Us
If you encounter an accessibility issue or have accessibility-related questions, please contact us:
${CONTACT_BLOCK}
We will make reasonable efforts to review accessibility feedback and respond as appropriate.`;

export const CONTACT_LEGAL_INFORMATION = `TravelBio Contact & Legal Information
Effective Date: July 4, 2026

1. Company Information
TravelBio is a travel planning and travel journaling platform designed to help users discover destinations, organize trips, preserve travel memories, and connect with third-party travel booking providers.
At launch, TravelBio is available through our website and iOS application. Additional platforms may be supported in the future.

2. Business Information
Business Name: TravelBio
Legal Entity: To be updated upon final business registration.
Business Location: Vermont, United States

3. Website
Official Website
https://mytravelbio.com

4. Contact Information
For general questions, customer support, business inquiries, or legal matters, please contact us at:
Email
info@mytravelbio.com
We aim to respond to inquiries as promptly as reasonably possible.

5. Intellectual Property
Unless otherwise noted, the TravelBio name, branding, logos, graphics, software, designs, original written content, and other proprietary materials are owned by TravelBio and are protected by applicable intellectual property laws.
Unauthorized use, reproduction, distribution, or modification of our intellectual property is prohibited without prior written permission.

6. Third-Party Services
TravelBio may integrate with or provide links to third-party services, including travel providers, mapping services, analytics providers, authentication providers, cloud hosting providers, and other technology partners.
Those services operate independently and are governed by their own terms, privacy policies, and business practices.

7. Legal Documents
The following legal documents govern the use of TravelBio and are incorporated by reference where applicable:
Privacy Policy
Terms of Service
Affiliate Disclosure
Booking & Pricing Disclaimer
Cookie Policy
User Content & Copyright Policy
Travel Information Disclaimer
Community Guidelines
Acceptable Use Policy
Accessibility Statement
Contact & Legal Information
DMCA Copyright Policy
Additional legal documents may be added or updated as TravelBio expands its services.

8. Changes to Legal Documents
We may revise our legal documents periodically to reflect changes in our services, legal requirements, technology, or business practices.
When material updates are made, the Effective Date of the applicable document will be updated.
Continued use of TravelBio following the effective date of revised documents constitutes acceptance of those updates.

9. Questions
If you have questions regarding any TravelBio legal document, policy, or disclosure, please contact us using the information provided on this page.`;

export const DMCA_TAKEDOWN_POLICY = `TravelBio DMCA Copyright Policy
Effective Date: July 4, 2026

1. Purpose
TravelBio respects the intellectual property rights of others and expects our users to do the same.
This DMCA Copyright Policy explains how copyright owners may report alleged copyright infringement occurring on the TravelBio website or iOS application and how TravelBio responds to such notices.

2. Reporting Copyright Infringement
If you believe that content available through TravelBio infringes your copyrighted work, you may submit a written copyright notice containing the following information:
Your full name and contact information.
Identification of the copyrighted work you claim has been infringed.
Identification of the material you believe is infringing, including enough information for us to locate it.
A statement that you have a good-faith belief the use of the material is not authorized by the copyright owner, its agent, or the law.
A statement, under penalty of perjury, that the information provided is accurate and that you are the copyright owner or authorized to act on the copyright owner's behalf.
Your physical or electronic signature.
Incomplete notices may delay our ability to review your request.

3. How to Submit a Notice
Copyright notices should be sent to:
MyTravelBio LLC (d/b/a TravelBio)
Email: info@mytravelbio.com
Website: https://mytravelbio.com
Business Location: Vermont, United States
We encourage electronic submission whenever possible.

4. Our Response
Upon receiving a notice that appears complete and valid, TravelBio may:
Review the reported material.
Remove or disable access to the content while the claim is evaluated.
Notify the user who uploaded the content, when appropriate.
Take additional action if warranted under our policies or applicable law.
Submitting a copyright notice does not automatically guarantee removal of content.

5. Counter-Notification
If your content has been removed because of a copyright complaint and you believe the removal was made in error or that you have the legal right to use the material, you may submit a counter-notification containing:
Your name and contact information.
Identification of the material that was removed.
A statement, under penalty of perjury, that you have a good-faith belief the material was removed due to mistake or misidentification.
A statement that you consent to the jurisdiction of the appropriate court, where required by applicable law.
Your physical or electronic signature.
TravelBio will review counter-notifications and respond in accordance with applicable law.

6. Repeat Infringers
TravelBio reserves the right to suspend or permanently terminate accounts belonging to users who repeatedly infringe the intellectual property rights of others.
Whether a user is considered a repeat infringer will be determined based on the circumstances and our reasonable judgment.

7. Misrepresentations
Submitting false or misleading copyright notices or counter-notifications may have legal consequences.
Please ensure that any submission is truthful, complete, and made in good faith.

8. Changes to This Policy
We may update this DMCA Copyright Policy from time to time.
Material changes will be reflected by updating the Effective Date shown at the top of this document.
Continued use of TravelBio after updates become effective constitutes acceptance of the revised policy.

9. Contact Us
If you have questions regarding this DMCA Copyright Policy, please contact us:
${CONTACT_BLOCK}`;

const EFFECTIVE_DATE = "July 4, 2026";

export const POLICIES = [
  { slug: "privacy-policy",                title: "Privacy Policy",                   effective_date: EFFECTIVE_DATE, body: PRIVACY_POLICY },
  { slug: "terms-of-service",              title: "Terms of Service",                 effective_date: EFFECTIVE_DATE, body: TERMS_OF_SERVICE },
  { slug: "affiliate-disclosure",          title: "Affiliate Disclosure",             effective_date: EFFECTIVE_DATE, body: AFFILIATE_DISCLOSURE },
  { slug: "booking-pricing-disclaimer",    title: "Booking & Pricing Disclaimer",     effective_date: EFFECTIVE_DATE, body: BOOKING_PRICING_DISCLAIMER },
  { slug: "cookie-policy",                 title: "Cookie Policy",                    effective_date: EFFECTIVE_DATE, body: COOKIE_POLICY },
  { slug: "user-content-copyright-policy", title: "User Content & Copyright Policy",  effective_date: EFFECTIVE_DATE, body: USER_CONTENT_COPYRIGHT_POLICY },
  { slug: "travel-information-disclaimer", title: "Travel Information Disclaimer",    effective_date: EFFECTIVE_DATE, body: TRAVEL_INFORMATION_DISCLAIMER },
  { slug: "community-guidelines",          title: "Community Guidelines",             effective_date: EFFECTIVE_DATE, body: COMMUNITY_GUIDELINES },
  { slug: "acceptable-use-policy",         title: "Acceptable Use Policy",            effective_date: EFFECTIVE_DATE, body: ACCEPTABLE_USE_POLICY },
  { slug: "accessibility-statement",       title: "Accessibility Statement",          effective_date: EFFECTIVE_DATE, body: ACCESSIBILITY_STATEMENT },
  { slug: "contact-legal-information",     title: "Contact & Legal Information",      effective_date: EFFECTIVE_DATE, body: CONTACT_LEGAL_INFORMATION },
  { slug: "dmca-takedown-policy",          title: "DMCA/Copyright Takedown Policy",   effective_date: EFFECTIVE_DATE, body: DMCA_TAKEDOWN_POLICY },
];

export function getPolicy(slug) {
  return POLICIES.find((p) => p.slug === slug) || null;
}
